<script setup lang="ts">
import type { Direction } from '@/components/ui/dropdown.vue'

type SocialMedia = 'facebook' | 'twitter' | 'linkedin' | 'mail'

const props = defineProps<{
    direction: Direction
    title?: string
    bordered?: boolean
}>()

/**
 * Translations
 */
const { t } = useI18n()

/**
 * Native share function.
 */
const { isSupported, share } = useShare()

/**
 * Popup height.
 */
const popupHeight = 560

/**
 * Popup width.
 */
const popupWidth = 770

/**
 * Get the sharing URL depending on selected type.
 */
function getSocialMediaUrl(type: SocialMedia) {
    const address = location.href

    switch (type) {
        case 'facebook':
            return `https://www.facebook.com/sharer/sharer.php?u=${address}`
        case 'twitter':
            return `https://twitter.com/intent/tweet?url=${address}`
        case 'linkedin':
            return `https://www.linkedin.com/shareArticle?mini=true&url=${address}`
        case 'mail':
            return `mailto:?body=${address}`
    }
}

/**
 * Open the sharing popup.
 */
function openPopup(url: string) {
    const left = window.innerWidth / 2 - popupWidth / 2
    const top = window.innerHeight / 2 - popupHeight / 2

    return window.open(
        url,
        '_blank',
        `height=${popupHeight},width=${popupWidth},top=${top},left=${left}`
    )
}

/**
 * Open a sharing window.
 */
function openSocialMediaShare(event: Event, type: SocialMedia) {
    const url = getSocialMediaUrl(type)

    if (type === 'mail') {
        const a = document.createElement('a')
        a.href = url
        a.click()
        return event.preventDefault()
    }

    const popup = openPopup(url)

    const blocked =
        !popup || popup.closed || typeof popup.closed === 'undefined'

    if (!blocked) event.preventDefault()

    return false
}

/**
 * Menu items for non-native share.
 */
const items = [
    {
        label: 'Mail',
        action: (event: Event) => openSocialMediaShare(event, 'mail')
    },
    {
        label: 'Facebook',
        action: (event: Event) => openSocialMediaShare(event, 'facebook')
    },
    {
        label: 'Twitter',
        action: (event: Event) => openSocialMediaShare(event, 'twitter')
    },
    {
        label: 'LinkedIn',
        action: (event: Event) => openSocialMediaShare(event, 'linkedin')
    }
] as MenuItem[]

/**
 * Use the native Share API.
 */
function nativeShare() {
    share({
        title: props.title,
        url: location.href
    })
}
</script>

<template>
    <button
        v-if="isSupported"
        :title="t('share')"
        type="button"
        @click="nativeShare"
    >
        <ShareIcon
            class="ease h-6 transition-colors duration-500 hover:text-deep-blue"
            :class="{
                'h-8 rounded border border-deep-blue p-1': bordered
            }"
        />
    </button>

    <UiDropdown
        v-else
        :title="t('share')"
        :items="items"
        :direction="direction"
    >
        <template #trigger-content>
            <ShareIcon
                class="ease h-6 transition-colors duration-500 hover:text-deep-blue"
                :class="{
                    'h-8 rounded border border-deep-blue p-1': bordered
                }"
            />
        </template>
    </UiDropdown>
</template>